import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useEffect, useState } from 'react'
import { oncallQuery, oncallUsersQuery, type OncallGroup, type OncallUser } from '../api/easeedeskBoards'
import {
  addOncallUser,
  editOncallUser,
  oncallCandidatesQuery,
  openOncallTicket,
  removeOncallUser,
  type OncallUserForm,
} from '../api/easeedeskOncall'
import { DatePicker } from '../ui/DatePicker'
import { Modal } from '../ui/Modal'
import { SelectPicker } from '../ui/SelectPicker'
import { Content } from '../ui/Shell'
import { toast } from '../ui/notify'

/**
 * "Oncall Users" - `Easeedesk::oncall`, machetele
 * `views/version-3/easeedesk/oncall/index.php` si `oncall/users.php`.
 *
 * Acelasi titlu, aceleasi coloane, aceeasi casuta galbena cand nu exista
 * setari si aceeasi casuta albastra cand un grup n-are pe nimeni.
 *
 * SCRIERILE, acum si ele: "Add user" (fereastra cu grup, om, doua zile si
 * telefon), creionul de pe rand (aceeasi fereastra, fara grup, ca in portal)
 * si cosul, cu aceeasi intrebare inainte. Capetele sunt in
 * `api/easeedeskOncall.ts`.
 *
 * "Open ticket" (verde, sub tabel, ca in macheta): aceeasi intrebare ca in
 * portal, apoi `POST easeedesk/oncall/ticket` - tichetul de garda, cu oamenii
 * de garda in descriere (`OncallTicket` in API).
 */

const EMPTY: OncallUserForm = { user_id: 0, date_from: '', date_to: '', oncall_phone: '' }

/** Fereastra de adaugare si cea de editare, acelasi formular ca in portal. */
function OncallUserModal({
  open,
  title,
  groups,
  lockedGroup,
  initial,
  saving,
  error,
  onClose,
  onSave,
}: {
  open: boolean
  title: string
  /** Casuta de grup: numai la adaugare, ca in macheta. */
  groups: OncallGroup[] | null
  /** La editare, grupul randului: casuta de oameni se umple din el. */
  lockedGroup: number
  initial: OncallUserForm
  saving: boolean
  error: string
  onClose(): void
  onSave(group: number, form: OncallUserForm): void
}) {
  const [group, setGroup] = useState(lockedGroup)
  const [form, setForm] = useState(initial)

  /* Fereastra se umple la fiecare deschidere, ca `shown.bs.modal` in portal,
     si se goleste la inchidere (`hidden.bs.modal` facea `form.reset()`). */
  useEffect(() => {
    if (!open) return
    setGroup(lockedGroup)
    setForm(initial)
    /* `initial` si `lockedGroup` sunt alese o data, la deschidere. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const candidates = useQuery(oncallCandidatesQuery(group))
  const people = candidates.data?.users ?? []

  return (
    <Modal id={groups ? 'newOncallUserModal' : 'editOncallUserModal'} open={open} onClose={onClose} title={
      <>
        <i className="fas fa-phone-volume" aria-hidden="true"></i> {title}
      </>
    }>
      <form onSubmit={(e) => e.preventDefault()}>
        {error !== '' ? <div className="alert alert-danger">{error}</div> : null}
        {groups ? (
          <div className="form-group">
            <label htmlFor="oncall-group" className="control-label">
              Group:
            </label>
            <SelectPicker
              id="oncall-group"
              className="form-control select-event"
              title="Select.."
              options={groups.map((g) => ({ value: String(g.id), label: g.name }))}
              value={group > 0 ? String(group) : ''}
              onChange={(next) => {
                setGroup(Number(next))
                /* Ca in portal: schimbarea grupului reface lista de oameni,
                   deci alegerea de dinainte nu mai are ce cauta in formular. */
                setForm((f) => ({ ...f, user_id: 0 }))
              }}
            />
          </div>
        ) : null}
        <div className="form-group">
          <label htmlFor="oncall-user" className="control-label">
            User:
          </label>
          <SelectPicker
            id="oncall-user"
            className="form-control select-values"
            title="no user selected..."
            options={people.map((p) => ({ value: String(p.id), label: p.name }))}
            value={form.user_id > 0 ? String(form.user_id) : ''}
            disabled={group < 1}
            onChange={(next) => setForm((f) => ({ ...f, user_id: Number(next) }))}
          />
        </div>
        <div className="form-group">
          <label htmlFor="oncall-date-from" className="control-label">
            Date from:
          </label>
          <DatePicker name="date_from" value={form.date_from} onChange={(next) => setForm((f) => ({ ...f, date_from: next }))} />
        </div>
        <div className="form-group">
          <label htmlFor="oncall-date-to" className="control-label">
            Date to:
          </label>
          <DatePicker name="date_to" value={form.date_to} onChange={(next) => setForm((f) => ({ ...f, date_to: next }))} />
        </div>
        <div className="form-group">
          <label htmlFor="oncall-phone" className="control-label">
            Oncall phone:
          </label>
          <input
            id="oncall-phone"
            type="text"
            className="form-control"
            name="oncall_phone"
            value={form.oncall_phone}
            onChange={(e) => setForm((f) => ({ ...f, oncall_phone: e.target.value }))}
          />
        </div>
      </form>
      <div className="modal-footer">
        <button type="button" className="btn btn-default" onClick={onClose}>
          Close
        </button>
        <button type="button" className="btn btn-primary" disabled={saving} onClick={() => onSave(group, form)}>
          {groups ? 'Create' : 'Update'}
        </button>
      </div>
    </Modal>
  )
}

function GroupUsers({ group, onEdit, onRemove }: { group: OncallGroup; onEdit(user: OncallUser): void; onRemove(user: OncallUser): void }) {
  const q = useQuery(oncallUsersQuery(group.id))
  const users = q.data?.users ?? []

  if (q.isPending) return <div className="col-md-10 oncall-users" data-group={group.id}></div>

  return (
    <div className="col-md-10 oncall-users" data-group={group.id}>
      {users.length === 0 ? (
        <div className="alert alert-info">
          <i className="fas fa-info-circle"></i> No user selected from this group.
        </div>
      ) : (
        <table className="table table-hover" style={{ fontSize: '12px' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', width: '20%' }}>User</th>
              <th style={{ textAlign: 'left', width: '20%' }}>From</th>
              <th style={{ textAlign: 'left', width: '20%' }}>To</th>
              <th style={{ textAlign: 'left', width: '20%' }}>Oncall phone</th>
              <th style={{ width: '20%' }}>Edit</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.name}</td>
                <td>{user.date_from}</td>
                <td>{user.date_to}</td>
                <td>{user.phone}</td>
                <td style={{ textAlign: 'center' }}>
                  <div className="btn-group" role="group" aria-label="controls">
                    <button
                      type="button"
                      className="btn btn-success btn-xs editOncallUser"
                      title="Edit"
                      onClick={() => onEdit(user)}
                    >
                      <span className="fas fa-pencil-alt" aria-hidden="true"></span>
                    </button>
                  </div>
                  <div className="btn-group" role="group" aria-label="controls">
                    <button
                      type="button"
                      className="btn btn-danger btn-xs removeOncallUser"
                      title="Remove"
                      onClick={() => onRemove(user)}
                    >
                      <span className="fa fa-trash" aria-hidden="true"></span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export function OncallPage() {
  const q = useQuery(oncallQuery)
  const groups = q.data?.groups ?? []
  const client = useQueryClient()

  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<{ group: number; row: number; form: OncallUserForm } | null>(null)
  const [error, setError] = useState('')

  /* Ca `_loadContent()` din portal: dupa fiecare scriere, panourile pe grup
     se cer din nou, toate. */
  const reload = () => void client.invalidateQueries({ queryKey: ['easeedesk', 'oncall'] })

  const create = useMutation({
    mutationFn: ({ group, form }: { group: number; form: OncallUserForm }) => addOncallUser(group, form),
    onSuccess: () => {
      setAdding(false)
      setError('')
      reload()
      toast('New oncall user successfully added.', 'Success')
    },
    onError: (e: Error) => setError(e.message),
  })

  const change = useMutation({
    mutationFn: ({ row, form }: { row: number; form: OncallUserForm }) => editOncallUser(row, form),
    onSuccess: () => {
      setEditing(null)
      setError('')
      reload()
      toast('Oncall user successfully updated.', 'Success')
    },
    onError: (e: Error) => setError(e.message),
  })

  const openTicket = useMutation({
    mutationFn: () => openOncallTicket(),
    onSuccess: () => toast('New oncall ticket successfully opened', 'Success'),
    onError: (e: Error) => toast(e.message, 'Error', 'error'),
  })

  const drop = useMutation({
    mutationFn: (row: number) => removeOncallUser(row),
    onSuccess: () => {
      reload()
      toast('Oncall user removed', 'Success')
    },
    onError: (e: Error) => toast(e.message, 'Error', 'error'),
  })

  return (
    <Content>
      <h4>Oncall Users</h4>
      <hr />
      {q.isSuccess && groups.length === 0 ? (
        <div className="alert alert-warning">
          <i className="fas fa-exclamation-triangle"></i>
          There are no settings created for oncall users. Please complete the settings first.
        </div>
      ) : null}
      {groups.length > 0 ? (
        <>
          <div className="row">
            <div className="col-md-12 aright">
              <button
                type="button"
                className="btn btn-default btn-sm btn-primary"
                onClick={() => {
                  setError('')
                  setAdding(true)
                }}
              >
                <i className="fas fa-plus" aria-hidden="true"></i> Add user
              </button>
            </div>
          </div>
          <div className="row" style={{ paddingTop: '2%' }}>
            {groups.map((group) => (
              <Fragment key={group.id}>
                <div className="col-md-2">
                  <h5>{group.name}</h5>
                </div>
                <GroupUsers
                  group={group}
                  onEdit={(user) => {
                    setError('')
                    setEditing({
                      group: group.id,
                      row: user.id,
                      form: {
                        user_id: user.user_id,
                        date_from: user.date_from,
                        date_to: user.date_to,
                        oncall_phone: user.phone,
                      },
                    })
                  }}
                  onRemove={(user) => {
                    /* Aceeasi intrebare ca swal-ul din portal. */
                    if (!window.confirm('Remove this oncall user? You will not be able to recover this record!')) return
                    drop.mutate(user.id)
                  }}
                />
                <hr className="dotted" style={{ clear: 'both' }} />
              </Fragment>
            ))}
          </div>
          <div className="row">
            <div className="col-md-12 aright">
              <button
                type="button"
                className="btn btn-default btn-sm btn-success"
                id="openOncallTicket"
                disabled={openTicket.isPending}
                onClick={() => {
                  if (!window.confirm('Open new oncall ticket? You will open a new ticket with oncall users details!')) return
                  openTicket.mutate()
                }}
              >
                <i className="fas fa-ticket-alt" aria-hidden="true"></i> Open ticket
              </button>
            </div>
          </div>
          <OncallUserModal
            open={adding}
            title="New oncall user"
            groups={groups}
            lockedGroup={0}
            initial={EMPTY}
            saving={create.isPending}
            error={error}
            onClose={() => setAdding(false)}
            onSave={(group, form) => create.mutate({ group, form })}
          />
          <OncallUserModal
            open={editing !== null}
            title="Edit oncall user"
            groups={null}
            lockedGroup={editing?.group ?? 0}
            initial={editing?.form ?? EMPTY}
            saving={change.isPending}
            error={error}
            onClose={() => setEditing(null)}
            onSave={(_group, form) => {
              if (editing) change.mutate({ row: editing.row, form })
            }}
          />
        </>
      ) : null}
    </Content>
  )
}
